import Vue from 'vue';
import _ from 'lodash';
import moment from 'moment';
import Api from "services/Api";
import store from 'store/index';
import {previousValid} from 'store/generators/step';
import {requestPayload} from 'store/modules/claim';
import Validator from 'services/Validator';
import {
    lifeExpectancyTemplate,
    purchaseTemplate,
    otherFutureLossesCareTemplate,
    otherFutureLossesServicesTemplate
} from 'store/modules/shared/templates';
import {validDate} from 'components/form/validator/validations';
import {determineLife} from 'services/personFunctions';

/**
 * Mutation Types
 *
 * @type {Object}
 */
const mutationTypes = {
    RESET: 'RESET',
    SET: 'SET',
    SET_PENSION: 'SET_PENSION',
    SET_CARE: 'SET_CARE',
    CLEAR_STEP: 'CLEAR_STEP',
    ADD_SERVICE: 'ADD_SERVICE',
    SET_SERVICES: 'SET_SERVICES',
    ADD_PURCHASE: 'ADD_PURCHASE',
    SET_PURCHASE: 'SET_PURCHASE',
    REMOVE_SERVICE: 'REMOVE_SERVICE',
    REMOVE_PURCHASE: 'REMOVE_PURCHASE',
    ADD_CARE_PERIOD: 'ADD_CARE_PERIOD',
    SET_CARE_PERIOD: 'SET_CARE_PERIOD',
    REMOVE_CARE_PERIOD: 'REMOVE_CARE_PERIOD',
    UPDATE_FROM_SERVER: 'UPDATE_FROM_SERVER',
    UPDATE_CARE_LIFE_EXPECTANCY: 'UPDATE_CARE_LIFE_EXPECTANCY',
    UPDATE_SERVICES_LIFE_EXPECTANCY: 'UPDATE_SERVICES_LIFE_EXPECTANCY',
    UPDATE_PURCHASES_LIFE_EXPECTANCY: 'UPDATE_PURCHASES_LIFE_EXPECTANCY'
};

/**
 * Validation Rules.
 *
 * @type {Object}
 */
const rules = {
    'hasOtherFutureLosses': {type: 'text', rules: 'required'},
};

/**
 * Cycle over an object and dispatch an update for each key.
 *
 * @param {function} dispatch
 * @param {string} action
 * @param {{}} obj
 * @param {number|null} [index]
 *
 * @return {Promise}
 */
const updateObject = (dispatch, action, obj, index = null) => {
    const proms = Object.keys(obj).map(key => dispatch(action, {key, value: obj[key], index}));
    return Promise.all(proms);
};

/**
 * Throttle update of person life expectancy to allow for typing
 */
export const throttleUpdateLife = (dispatch, action, person, lifeExpectancy, index) => {

    if (validDate(person.dateOfBirth)) {
        if (lifeExpectancy.method == 'assumed_percentage_of_life') {
            lifeExpectancy.method = 'percentage_of_normal_life_expectancy';
        }

        if (lifeExpectancy.method == 'assumed_reduction_in_life') {
            lifeExpectancy.method = 'percentage_reduction_in_normal_life_expectancy';
        }

        let assumedSettlement = store.getters['personal/claimant/getClaimInfo']('dateOfAssumedSettlement');


        determineLife(assumedSettlement, person, lifeExpectancy)
            .then(updatedLife => {
                return updateObject(dispatch, action, updatedLife, index);
            })
    }
};

const isPensionUserInputValid = (state) => {
    let pension = state.pension;

    if (
        !pension.butForRetirementDate ||
        !pension.butForRetirementAge ||
        pension.butForRetirementAge < 50 ||
        !pension.pensionMethod ||
        // !pension.retirementDate || // We don't need this unless we are using the Longden method
        !pension.butForPensionAnnual ||
        pension.pensionAnnual === null ||
        pension.pensionAnnual === '' ||
        !pension.pensionLossAnnual ||
        pension.lumpSumLoss === null ||
        pension.pensionAnnual > pension.butForPensionAnnual
    ) {
        return false;
    }

    // If Lump Sum Pension Loss, there is additional validation:
    //  - There must be a 'But For' Lump Sum
    //  - The Actual Pension Lump Sum must not be null.
    if (pension.lumpSumLoss
        && (
            !pension.butForPensionLumpSum ||
            pension.pensionLumpSum === null ||
            pension.pensionLumpSum === '' ||
            pension.dateLumpSumPaymentReceived === null ||
            pension.dateLumpSumPaymentReceived === '' ||
            pension.pensionLumpSum > pension.butForPensionLumpSum
        )
    ) {
        return false;
    }

    return true;
};

const isPensionValid = (state) => {
    let pension = state.pension;
    // Alwways valid if future loss of pension is false.
    if (state.futureLossOfPension === false) {
        return true;
    }

    // Guard against user input
    if (!isPensionUserInputValid(state)) {
        return false;
    }

    // Guard against missing calculations.
    if (
        !pension.pensionLossMultiplier ||
        !pension.totalLoss
    ) {
        return false;
    }

    // Guard against lump sum specific calculations
    if (pension.lumpSumLoss
        && (
            pension.lumpSumLossPresentValue === null ||
            pension.longdenFactor === null
        )
    ) {
        return false;
    }

    return true;
};

const isCareValid = (state) => {
    if (state.futureLossOfCare === null) {
        return false;
    }

    if (state.futureLossOfCare === false) {
        return true;
    }

    return state.care.reduce((valid, care) => !valid ? valid : careValid(care), true);
};

const careValid = (care) => {
    return (
        care.description !== null &&
        care.description !== '' &&
        care.careStartDate !== null &&
        care.careStartDate !== '' &&
        care.careEndDate !== null &&
        care.careEndDate !== '' &&
        care.duration !== 0 &&
        care.multiplier !== 0 &&
        care.multiplicand !== 0 &&
        care.multiplicand !== '' &&
        care.lossesForLife !== null &&
        care.futureCareTotal !== 0 &&
        moment(care.careStartDate, 'DD/MM/YYYY')
            .isBefore(moment(care.careEndDate, 'DD/MM/YYYY')) &&
        care.mortalityMultiplier !== null);
};

const isServicesValid = (state) => {
    if (state.futureLossOfServices === null) {
        return false;
    }

    if (state.futureLossOfServices === false) {
        return true;
    }

    return state.services.reduce((valid, service) => !valid ? valid : servicesValid(service), true);
};

const servicesValid = (service) => {
    return (
        service.description !== null &&
        service.description !== '' &&
        service.servicesStartDate !== null &&
        service.servicesStartDate !== '' &&
        service.servicesEndDate !== null &&
        service.servicesEndDate !== '' &&
        service.duration !== 0 &&
        service.multiplier !== 0 &&
        service.multiplicand !== 0 &&
        service.multiplicand !== '' &&
        service.lossesForLife !== null &&
        service.futureServicesTotal !== 0 &&
        moment(service.servicesStartDate, 'DD/MM/YYYY')
            .isBefore(moment(service.servicesEndDate, 'DD/MM/YYYY')) &&
        service.mortalityMultiplier !== null
    );
};

const isPurchasesValid = (state) => {
    if (state.futurePurchases === null) {
        return false;
    }

    if (state.futurePurchases === false) {
        return true;
    }

    return state.purchases.reduce((valid, purchase) => !valid ? valid : purchaseValid(purchase), true);
};

const purchaseValid = (purchase) => {
    if (purchase.type !== null && purchase.type === 'one') {
        return (
            purchase.description !== null &&
            purchase.description !== '' &&
            purchase.type !== null &&
            moment(store.getters['personal/claimant/getClaimInfo']('dateOfAssumedSettlement'), 'DD/MM/YYYY')
                .isSameOrBefore(moment(purchase.purchaseDate, 'DD/MM/YYYY')) &&
            purchase.purchaseDate !== null &&
            purchase.purchaseDate !== '' &&
            purchase.resaleValue < purchase.multiplicand &&
            purchase.ageAtPurchase !== 0 &&
            purchase.ageAtPurchase !== '' &&
            purchase.multiplicand !== 0 &&
            purchase.discount !== 0 &&
            purchase.total !== 0
        );
    }

    if (purchase.type !== null && purchase.type === 'recurring') {
        return (
            purchase.description !== null &&
            purchase.description !== '' &&
            purchase.type !== null &&
            moment(store.getters['personal/claimant/getClaimInfo']('dateOfAssumedSettlement'), 'DD/MM/YYYY')
                .isSameOrBefore(moment(purchase.purchaseDate, 'DD/MM/YYYY')) &&
            purchase.purchaseDate !== null &&
            purchase.purchaseDate !== '' &&
            purchase.purchaseEnd !== null &&
            purchase.purchaseEnd !== '' &&
            purchase.frequency !== null &&
            purchase.frequency !== '' &&
            purchase.resaleValue < purchase.multiplicand &&
            purchase.ageAtPurchase !== 0 &&
            purchase.ageAtPurchase !== '' &&
            purchase.multiplicand !== 0 &&
            purchase.discount !== 0 &&
            purchase.total !== 0
        );
    }

    if (purchase.type !== null && purchase.type === 'continuous') {
        return (
            purchase.description !== null &&
            purchase.description !== '' &&
            purchase.type !== null &&
            moment(store.getters['personal/claimant/getClaimInfo']('dateOfAssumedSettlement'), 'DD/MM/YYYY')
                .isSameOrBefore(moment(purchase.purchaseDate, 'DD/MM/YYYY')) &&
            purchase.purchaseDate !== null &&
            purchase.purchaseDate !== '' &&
            purchase.purchaseEnd !== null &&
            purchase.purchaseEnd !== '' &&
            purchase.ageAtPurchase !== 0 &&
            purchase.ageAtPurchase !== '' &&
            purchase.multiplicand !== 0 &&
            purchase.discount !== 0 &&
            purchase.total !== 0
        );
    }
};

const getPurchasesTotal = (state) => {
    let totals = state.purchases.map((purchase) => {
        return purchase.total;
    });

    return totals.reduce((carry, item) => carry + item, 0);
};

const getCareTotal = (state) => {
    let totals = state.care.map((period) => {
        return period.futureCareTotal;
    });

    return totals.reduce((carry, item) => carry + item, 0);
};

const getServicesTotal = (state) => {
    let totals = state.services.map((period) => {
        return period.futureServicesTotal;
    });

    return totals.reduce((carry, item) => carry + item, 0);
};

/**
 * Initial State.
 *
 * @return {Object}
 */
function initialState() {
    return {
        // Are there future losses?
        hasOtherFutureLosses: null,

        // Is there future loss of pension?
        futureLossOfPension: null,
        pension: {
            // Ill Health Early Retirement / Disability Pension Scheme
            illHealthDisabilityPension: false,

            // Simple Method or Longden Method
            pensionMethod: null,

            //Period of Accelerated Receipt
            acceleratedReceipt: null,

            // Retirement Dates ('But For' and Actual)
            butForRetirementDate: null,
            butForRetirementAge: null,
            retirementDate: null,
            multiplierToRetirement: null,

            // Applies to Lump Sum and Annual Payments
            lifeMultiplier: null,
            discountFactor: null,

            // Is there a lump sum pension payment?
            lumpSumLoss: null,
            butForPensionLumpSum: 0,
            pensionLumpSum: 0,
            longdenFactor: null,
            lumpSumLossValue: null,
            lumpSumLossPresentValue: null,
            dateLumpSumPaymentReceived: null,

            // Annual Pension ('But For' and Actual)
            butForPensionAnnual: 0,
            pensionAnnual: 0,

            // Pension Loss per Annum ('But For' - 'Actual')
            pensionLossAnnual: null,

            // Pension Loss Multiplier
            pensionLossMultiplier: null,

            // Total Loss Annual (Pension Loss Annual * Pension Loss Multiplier)
            totalLossAnnual: null,

            // Total Loss Annual + Lump Sum Loss
            totalLoss: null,
        },

        // Are there future losses for care?
        futureLossOfCare: null,
        care: [],
        careLifeExpectancy: lifeExpectancyTemplate(),

        // Are there future losses for services?
        futureLossOfServices: null,
        services: [],
        servicesLifeExpectancy: lifeExpectancyTemplate(),

        // Is there a one off future purchase?
        futurePurchases: null,
        purchases: [],
        purchasesLifeExpectancy: lifeExpectancyTemplate(),
    }
}

/**
 * Initial State
 *
 * @type {Object}
 */
const state = initialState();

/**
 * Getters.
 *
 * @type {Object}
 */
const getters = {
    value: state => key => state[key],
    pensionValue: state => key => state.pension[key],
    care: state => state.care,
    carePeriod: state => index => state.care[index],
    careValue: state => key => state.care[key],
    careTotal: state => getCareTotal(state),
    services: state => state.services,
    service: state => index => state.service[index],
    servicesTotal: state => getServicesTotal(state),
    servicesValue: state => key => state.services[key],
    purchaseValue: state => key => state.purchases[key],
    pensionUserInputValid: state => isPensionUserInputValid(state),
    pensionValid: state => isPensionValid(state),
    careValid: state => isCareValid(state),
    servicesValid: state => isServicesValid(state),
    purchasesValid: state => isPurchasesValid(state),
    purchasesTotal: state => getPurchasesTotal(state),
    pension: state => state.pension
};

// Actions
const actions = {
    /**
     * Save the step by submitting to the server.
     *
     * @param getters
     * @param dispatch
     * @param state
     * @param payload
     */
    save({getters, dispatch, state}, payload) {
        payload.data = state;

        Api.claims.save(payload).then((response) => {
            return response;
        }).then(() => store.dispatch('personal/save', requestPayload(0)))
    },

    /**
     * Loads the step being received from the Server, to be placed in the state.
     *
     * @param dispatch
     * @param action
     * @param commit
     * @param step
     */
    load({dispatch, action, commit}, step) {
        const data = _.deeply(_.mapKeys)(step, (value, key) => _.camelCase(key)).data;
        commit(mutationTypes.UPDATE_FROM_SERVER, {data});
    },

    /**
     * Performs any processing once the data from the server has been stored.
     * This is a good place to do calculations or dispatch other actions which
     * are used to refresh/update calculated data on other steps/components.
     *
     * @param commit
     * @param dispatch
     */
    process({commit, dispatch}) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 5,
            valid: true, // needs updating with real validity
        });
    },

    /**
     * Validate the step.
     *
     * @param commit
     * @param state
     * @param rootState
     */
    validate({commit, state, rootState}) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 5,
            valid: previousValid(rootState, 5)
                && (new Validator(state, rules)).validate(),
        });
    },

    /**
     * Resets the step back to defaults
     *
     * @param commit
     */
    reset({commit}) {
        store.dispatch('personal/step/setStepNumberValid', {
            stepNo: 5,
            valid: false,
        });

        commit(mutationTypes.RESET);
    },

    /**
     * Update Value
     *
     * @param commit
     * @param dispatch
     * @param key
     * @param value
     */
    set({commit, dispatch}, {key, value}) {
        commit(mutationTypes.SET, {key, value});

        if (key === 'hasOtherFutureLosses' && value === false) {
            commit(mutationTypes.CLEAR_STEP);
        }

        switch (key) {
            case 'futurePurchases':
                dispatch('updateFuturePurchases');
                break;
            case 'futureLossOfCare':
                dispatch('updateFutureCare');
                break;
            case 'futureLossOfServices':
                dispatch('updateFutureServices');
                break;
        }
    },

    /**
     * Update Care Value
     *
     * @param commit
     * @param dispatch
     * @param key
     * @param value
     */
    setPension({commit, dispatch}, {key, value}) {
        commit(mutationTypes.SET_PENSION, {key, value});

        dispatch('updatePension');
    },

    /**
     * Update Care Value
     *
     * @param commit
     * @param dispatch
     * @param key
     * @param value
     */
    setCare({commit, dispatch}, {key, value}) {
        commit(mutationTypes.SET_CARE, {key, value});

        dispatch('updateCare');
    },

    /**
     * Update Services Value
     *
     * @param commit
     * @param dispatch
     * @param key
     * @param value
     */
    setServices({commit, dispatch}, {key, value}) {
        commit(mutationTypes.SET_SERVICES, {key, value});

        dispatch('updateServices');
    },

    updatePension: _.debounce(({rootState, state, commit}) => {
        if (!isPensionUserInputValid(state)) {
            return;
        }

        return Api.calculators.monetary.pensionLoss(rootState.personal.claimant, state.pension).then((data) => {
            commit(mutationTypes.SET_PENSION, {key: 'discountFactor', value: data.discountFactor});
            commit(mutationTypes.SET_PENSION, {key: 'lifeMultiplier', value: data.lifeMultiplier});
            commit(mutationTypes.SET_PENSION, {key: 'longdenFactor', value: data.longdenFactor});
            commit(mutationTypes.SET_PENSION, {key: 'lumpSumLossPresentValue', value: data.lumpSumLossPresentValue});
            commit(mutationTypes.SET_PENSION, {key: 'multiplierToRetirement', value: data.multiplierToRetirement});
            commit(mutationTypes.SET_PENSION, {key: 'pensionLossMultiplier', value: data.pensionLossMultiplier});
            commit(mutationTypes.SET_PENSION, {key: 'totalLoss', value: data.totalLoss});
            commit(mutationTypes.SET_PENSION, {key: 'totalLossAnnual', value: data.totalLossAnnual});
            commit(mutationTypes.SET_PENSION, {key: 'acceleratedReceipt', value: data.acceleratedReceipt});
        });
    }, 500),

    /**
     * Update future purchases, add or clear
     *
     * @param state
     * @param dispatch
     * @param commit
     */
    updateFuturePurchases({state, dispatch, commit}) {
        if (state.futurePurchases) {
            return dispatch('addPurchase');
        }

        commit(mutationTypes.SET, {key: 'purchases', value: []});
    },

    /**
     * Update future care, add or clear
     *
     * @param state
     * @param dispatch
     * @param commit
     */
    updateFutureCare({state, dispatch, commit}) {
        if (state.futureLossOfCare) {
            return dispatch('addCare');
        }

        commit(mutationTypes.SET, {key: 'care', value: []});
    },

    /**
     * Update future services, add or clear
     *
     * @param state
     * @param dispatch
     * @param commit
     */
    updateFutureServices({state, dispatch, commit}) {
        if (state.futureLossOfServices) {
            return dispatch('addService');
        }

        commit(mutationTypes.SET, {key: 'services', value: []});
    },

    /**
     * Add a purchase
     *
     * @param commit
     */
    addPurchase({commit}) {
        commit(mutationTypes.ADD_PURCHASE);
    },

    /**
     * Add a purchase
     *
     * @param commit
     */
    addCare({commit}) {
        commit(mutationTypes.ADD_CARE_PERIOD);
    },

    /**
     * Add a service
     *
     * @param commit
     */
    addService({commit}) {
        commit(mutationTypes.ADD_SERVICE);
    },

    /**
     * Update Purchase Value
     *
     * @param commit
     * @param dispatch
     * @param index
     * @param key
     * @param value
     */
    setPurchase({commit, dispatch}, {index, key, value}) {
        commit(mutationTypes.SET_PURCHASE, {index, key, value});

        dispatch('updatePurchase', index);
    },

    /**
     * Update Purchase Value
     *
     * @param commit
     * @param dispatch
     * @param index
     * @param key
     * @param value
     */
    setCarePeriod({commit, dispatch}, {index, key, value}) {
        commit(mutationTypes.SET_CARE_PERIOD, {index, key, value});

        dispatch('updateCare', index);
    },

    /**
     * Remove a purchase
     *
     * @param state
     * @param commit
     * @param dispatch
     * @param index
     */
    removePurchase({state, commit, dispatch}, {index}) {
        commit(mutationTypes.REMOVE_PURCHASE, index);

        if (!state.purchases.length) {
            return dispatch('addPurchase');
        }
    },

    /**
     * Remove a care period
     *
     * @param state
     * @param commit
     * @param dispatch
     * @param index
     */
    removeCarePeriod({state, commit, dispatch}, {index}) {
        commit(mutationTypes.REMOVE_CARE_PERIOD, index);

        if (!state.care.length) {
            return dispatch('addCare');
        }
    },

    /**
     * Remove a service
     *
     * @param state
     * @param commit
     * @param dispatch
     * @param index
     */
    removeService({state, commit, dispatch}, {index}) {
        commit(mutationTypes.REMOVE_SERVICE, index);

        if (!state.services.length) {
            return dispatch('addService');
        }
    },

    /**
     * Hit purchase Api and update values
     *
     * @param state
     * @param commit
     * @param index
     */
    updatePurchase: _.debounce(({state, commit}, {index}) => {
        let purchase = null;

        if (state.purchases[index].purchaseDate === null ||
            state.purchases[index].purchaseDate === '' ||
            moment(state.purchases[index].purchaseDate, 'DD/MM/YYYY')
                .isBefore(moment(store.getters['personal/claimant/getClaimInfo']('dateOfAssumedSettlement'), 'DD/MM/YYYY')) ||
            state.purchases[index].multiplicand === 0 ||
            state.purchases[index].multiplicand === '' ||
            state.purchases[index].multiplicand < state.purchases[index].resaleValue ||
            state.purchases[index].multiplicand === null ||
            state.purchases[index].ageAtPurchase === 0 ||
            state.purchases[index].ageAtPurchase === '' ||
            state.purchases[index].resaleOption === null ||
            state.purchases[index].mortalityMultiplier === null) {
            return
        }

        if (state.purchases[index].type === 'one') {
            purchase = {
                dateOfPurchase: state.purchases[index].purchaseDate,
                discountRate: store.getters['personal/claimant/getClaimInfo']('discountRate'),
                dateOfBirth: store.getters['personal/claimant/getClaimantValue']('dateOfBirth'),
                assumedSettlement: store.getters['personal/claimant/getClaimInfo']('dateOfAssumedSettlement'),
                multiplicand: state.purchases[index].multiplicand,
                resaleValue: state.purchases[index].resaleOption ? state.purchases[index].resaleValue : 0,
                type: state.purchases[index].type,
                gender: store.getters['personal/claimant/getClaimantValue']('gender'),
                mortalityMultiplier: state.purchases[index].mortalityMultiplier
            };

            return Api.calculators.monetary.futurePurchase(purchase).then((data) => {
                commit(mutationTypes.SET_PURCHASE, {index, key: 'discount', value: data.discount});
                commit(mutationTypes.SET_PURCHASE, {index, key: 'total', value: data.total});
                commit(mutationTypes.SET_PURCHASE, {index, key: 'resaleTotal', value: data.resale_total});
            })
        }


        if (state.purchases[index].type === 'continuous' &&
            state.purchases[index].purchaseEnd !== null &&
            state.purchases[index].purchaseEnd !== '' &&
            moment(state.purchases[index].purchaseDate, 'DD/MM/YYYY')
                .isBefore(moment(state.purchases[index].purchaseEnd, 'DD/MM/YYYY')) &&
            state.purchases[index].mortalityMultiplier !== null
        ) {
            purchase = {
                dateOfPurchase: state.purchases[index].purchaseDate,
                discountRate: store.getters['personal/claimant/getClaimInfo']('discountRate'),
                dateOfBirth: store.getters['personal/claimant/getClaimantValue']('dateOfBirth'),
                assumedSettlement: store.getters['personal/claimant/getClaimInfo']('dateOfAssumedSettlement'),
                multiplicand: state.purchases[index].multiplicand,
                resaleValue: state.purchases[index].resaleOption ? state.purchases[index].resaleValue : 0,
                type: state.purchases[index].type,
                purchaseEnd: state.purchases[index].purchaseEnd,
                frequency: state.purchases[index].frequency,
                gender: store.getters['personal/claimant/getClaimantValue']('gender'),
                mortalityMultiplier: state.purchases[index].mortalityMultiplier
            };

            return Api.calculators.monetary.futurePurchase(purchase).then((data) => {
                commit(mutationTypes.SET_PURCHASE, {index, key: 'discount', value: data.discount});
                commit(mutationTypes.SET_PURCHASE, {index, key: 'total', value: data.total});
                commit(mutationTypes.SET_PURCHASE, {index, key: 'resaleTotal', value: data.resale_total});
            })
        }

        if (state.purchases[index].type === 'recurring' &&
            state.purchases[index].purchaseEnd !== null &&
            state.purchases[index].purchaseEnd !== '' &&
            moment(state.purchases[index].purchaseDate, 'DD/MM/YYYY')
                .isBefore(moment(state.purchases[index].purchaseEnd, 'DD/MM/YYYY')) &&
            state.purchases[index].frequency !== 0 &&
            state.purchases[index].frequency !== '' &&
            state.purchases[index].mortalityMultiplier !== null
        ) {
            purchase = {
                dateOfPurchase: state.purchases[index].purchaseDate,
                discountRate: store.getters['personal/claimant/getClaimInfo']('discountRate'),
                dateOfBirth: store.getters['personal/claimant/getClaimantValue']('dateOfBirth'),
                assumedSettlement: store.getters['personal/claimant/getClaimInfo']('dateOfAssumedSettlement'),
                multiplicand: state.purchases[index].multiplicand,
                resaleValue: state.purchases[index].resaleOption ? state.purchases[index].resaleValue : 0,
                type: state.purchases[index].type,
                purchaseEnd: state.purchases[index].purchaseEnd,
                frequency: state.purchases[index].frequency,
                gender: store.getters['personal/claimant/getClaimantValue']('gender'),
                mortalityMultiplier: state.purchases[index].mortalityMultiplier
            };

            return Api.calculators.monetary.futurePurchase(purchase).then((data) => {
                commit(mutationTypes.SET_PURCHASE, {index, key: 'discount', value: data.discount});
                commit(mutationTypes.SET_PURCHASE, {index, key: 'total', value: data.total});
                commit(mutationTypes.SET_PURCHASE, {index, key: 'resaleTotal', value: data.resale_total});
            })
        }
    }, 500),

    /**
     * Hit care Api and update values
     *
     * @param state
     * @param commit
     * @param index
     */
    updateCare: _.debounce(({state, commit}, {index}) => {

        if (state.care[index].careStartDate === null ||
            state.care[index].careStartDate === '' ||
            state.care[index].careEndDate === null ||
            state.care[index].careEndDate === '' ||
            state.care[index].multiplicand === 0 ||
            state.care[index].multiplicand === '' ||
            state.care[index].multiplicand === null ||
            state.care[index].lossesForLife === null ||
            state.care[index].mortalityMultiplier === null ||
            moment(state.care[index].careStartDate, 'DD/MM/YYYY')
                .isSameOrAfter(moment(state.care[index].careEndDate, 'DD/MM/YYYY'))) {
            return
        }

        let care = {
            multiplicand: state.care[index].multiplicand,
            startDate: state.care[index].careStartDate,
            gender: store.getters['personal/claimant/getClaimantValue']('gender'),
            discountRate: store.getters['personal/claimant/getClaimInfo']('discountRate'),
            dateOfBirth: store.getters['personal/claimant/getClaimantValue']('dateOfBirth'),
            assumedSettlement: store.getters['personal/claimant/getClaimInfo']('dateOfAssumedSettlement'),
            endDate: state.care[index].careEndDate,
            mortalityMultiplier: state.care[index].mortalityMultiplier,
        };

        return Api.calculators.monetary.otherFutureLosses(care).then((data) => {
            commit(mutationTypes.SET_CARE, {key: 'multiplier', value: data.multiplier, index});
            commit(mutationTypes.SET_CARE, {key: 'futureCareTotal', value: data.total, index});
            commit(mutationTypes.SET_CARE, {key: 'duration', value: data.duration, index});
        })

    }, 500),

    /**
     * Hit services Api and update values
     *
     * @param state
     * @param commit
     * @param index
     */
    updateServices: _.debounce(({state, commit}, {index}) => {

        if (state.services[index].servicesStartDate === null ||
            state.services[index].servicesStartDate === '' ||
            state.services[index].servicesEndDate === null ||
            state.services[index].servicesEndDate === '' ||
            state.services[index].multiplicand === 0 ||
            state.services[index].multiplicand === '' ||
            state.services[index].multiplicand === null ||
            state.services[index].lossesForLife === null ||
            state.services[index].mortalityMultiplier === null ||
            moment(state.services[index].servicesStartDate, 'DD/MM/YYYY')
                .isSameOrAfter(moment(state.services[index].servicesEndDate, 'DD/MM/YYYY'))) {
            return
        }

        let service = {
            multiplicand: state.services[index].multiplicand,
            startDate: state.services[index].servicesStartDate,
            gender: store.getters['personal/claimant/getClaimantValue']('gender'),
            discountRate: store.getters['personal/claimant/getClaimInfo']('discountRate'),
            dateOfBirth: store.getters['personal/claimant/getClaimantValue']('dateOfBirth'),
            assumedSettlement: store.getters['personal/claimant/getClaimInfo']('dateOfAssumedSettlement'),
            endDate: state.services[index].servicesEndDate,
            mortalityMultiplier: state.services[index].mortalityMultiplier,
        };

        return Api.calculators.monetary.otherFutureLosses(service).then((data) => {
            commit(mutationTypes.SET_SERVICES, {key: 'multiplier', value: data.multiplier, index});
            commit(mutationTypes.SET_SERVICES, {key: 'futureServicesTotal', value: data.total, index});
            commit(mutationTypes.SET_SERVICES, {key: 'duration', value: data.duration, index});
        })

    }, 500),

    /**
     * Get life expectancy for purchases
     *
     * @param state
     * @param dispatch
     * @param lifeExpectancy
     */
    updateLifeForPurchases({commit, dispatch}, {lifeExpectancy, index}) {
        return updateObject(dispatch, 'updatePurchasesLifeExpectancyValue', lifeExpectancy, index)
            .then(() => throttleUpdateLife(dispatch, 'updatePurchasesLifeExpectancyValue', store.getters['personal/claimant/claimant'], lifeExpectancy, index));
    },

    /**
     * Update a piece of the Claimant's Life Expectancy data.
     */
    updatePurchasesLifeExpectancyValue({commit, dispatch}, {index, key, value}) {
        commit(mutationTypes.UPDATE_PURCHASES_LIFE_EXPECTANCY, {index, key, value});
        dispatch('updatePurchase', {index});
    },

    /**
     * Get life expectancy for care
     *
     * @param state
     * @param dispatch
     * @param lifeExpectancy
     */
    updateLifeForCare({commit, dispatch}, {lifeExpectancy, index}) {
        return updateObject(dispatch, 'updateCareLifeExpectancyValue', lifeExpectancy, index)
            .then(() => throttleUpdateLife(dispatch, 'updateCareLifeExpectancyValue', store.getters['personal/claimant/claimant'], lifeExpectancy, index));
    },

    /**
     * Update a piece of the Claimant's Life Expectancy data.
     */
    updateCareLifeExpectancyValue({commit, dispatch}, {index, key, value}) {
        commit(mutationTypes.UPDATE_CARE_LIFE_EXPECTANCY, {index, key, value});

        if (key === 'dateOfDeath') {
            commit(mutationTypes.SET_CARE_PERIOD, {index, key: 'careEndDate', value});
        }

        dispatch('updateCare', {index});
    },

    /**
     * Get life expectancy for services
     *
     * @param state
     * @param dispatch
     * @param lifeExpectancy
     * @param index
     */
    updateLifeForServices({commit, dispatch}, {lifeExpectancy, index}) {
        return updateObject(dispatch, 'updateServicesLifeExpectancyValue', lifeExpectancy, index)
            .then(() => throttleUpdateLife(dispatch, 'updateServicesLifeExpectancyValue', store.getters['personal/claimant/claimant'], lifeExpectancy, index));
    },

    /**
     * Update a piece of the Claimant's Life Expectancy data.
     */
    updateServicesLifeExpectancyValue({commit, dispatch}, {index, key, value}) {
        commit(mutationTypes.UPDATE_SERVICES_LIFE_EXPECTANCY, {index, key, value});

        if (key === 'dateOfDeath') {
            commit(mutationTypes.SET_SERVICES, {index, key: 'servicesEndDate', value});
        }

        dispatch('updateServices', {index});
    },

    /**
     * If the user removes a lump sum then reset the fields in the state
     */
    resetLumpSum({commit, state}) {
        if (state.pension.lumpSumLoss === false) {
            commit(mutationTypes.SET_PENSION, {key: 'butForPensionLumpSum', value: 0});
            commit(mutationTypes.SET_PENSION, {key: 'pensionLumpSum', value: 0});
            commit(mutationTypes.SET_PENSION, {key: 'discountFactor', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'dateLumpSumPaymentReceived', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'acceleratedReceipt', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'lumpSumLossValue', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'lumpSumLossPresentValue', value: null});
        }
    },

    /**
     * reset pension
     */
    resetPension({commit, state}) {
        if (state.futureLossOfPension === false) {
            commit(mutationTypes.SET_PENSION, {key: 'illHealthDisabilityPension', value: false});
            commit(mutationTypes.SET_PENSION, {key: 'pensionMethod', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'dateLumpSumPaymentReceived', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'butForRetirementDate', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'retirementDate', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'multiplierToRetirement', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'lifeMultiplier', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'discountFactor', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'lumpSumLoss', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'butForPensionLumpSum', value: 0});
            commit(mutationTypes.SET_PENSION, {key: 'pensionLumpSum', value: 0});
            commit(mutationTypes.SET_PENSION, {key: 'longdenFactor', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'lumpSumLossValue', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'lumpSumLossPresentValue', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'butForPensionAnnual', value: 0});
            commit(mutationTypes.SET_PENSION, {key: 'pensionAnnual', value: 0});
            commit(mutationTypes.SET_PENSION, {key: 'pensionLossAnnual', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'pensionLossMultiplier', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'totalLossAnnual', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'totalLoss', value: null});
            commit(mutationTypes.SET_PENSION, {key: 'acceleratedReceipt', value: null});
        }
    },

    refresh({dispatch, state}) {
        let purchases = state.purchases.map((purchase, index) => dispatch('updatePurchase', {index}));
        let services = state.services.map((service, index) => dispatch('updateService', {index}));
        let care = state.care.map((service, index) => dispatch('updateCare', {index}));
        return Promise.all([
            Promise.all(purchases),
            Promise.all(services),
            Promise.all(care)
        ]);
    }
};

// Mutations
const mutations = {
    [mutationTypes.UPDATE_FROM_SERVER](state, {data}) {
        state.hasOtherFutureLosses = data.hasOtherFutureLosses;
        state.futureLossOfCare = data.futureLossOfCare;
        state.care = data.care;
        state.careLifeExpectancy = data.careLifeExpectancy;
        state.futureLossOfServices = data.futureLossOfServices;
        state.services = data.services;
        state.servicesLifeExpectancy = data.servicesLifeExpectancy;
        state.futurePurchases = data.futurePurchases;
        state.purchases = data.purchases;
        state.purchasesLifeExpectancy = data.purchasesLifeExpectancy;
        state.futureLossOfPension = data.futureLossOfPension;
        state.pension = data.pension;
    },
    /**
     * Reset teh state7p
     */
    [mutationTypes.RESET](state) {
        Object.assign(state, initialState());
    },
    /**
     * Set an object on the state.
     */
    [mutationTypes.SET](state, payload) {
        Vue.set(state, payload.key, payload.value);
    },
    /**
     * Set an object on the state.
     */
    [mutationTypes.SET_PENSION](state, payload) {
        Vue.set(state.pension, payload.key, payload.value);
    },
    /**
     * Set an object on the state.
     */
    [mutationTypes.SET_CARE](state, payload) {
        Vue.set(state.care[payload.index], payload.key, payload.value);
    },
    /**
     * Set an object on the state.
     */
    [mutationTypes.SET_SERVICES](state, payload) {
        Vue.set(state.services[payload.index], payload.key, payload.value);
    },
    /**
     * Set an object on the state.
     */
    [mutationTypes.SET_PURCHASE](state, payload) {
        Vue.set(state.purchases[payload.index], payload.key, payload.value);
    },
    /**
     * Set an object on the state.
     */
    [mutationTypes.SET_CARE_PERIOD](state, payload) {
        Vue.set(state.care[payload.index], payload.key, payload.value);
    },
    /**
     * Set an object on the state.
     */
    [mutationTypes.UPDATE_CARE_LIFE_EXPECTANCY](state, {key, value}) {
        state.careLifeExpectancy[key] = value;
        if (key === 'dateOfDeath') {
            state.care.careEndDate = value;
        }
    },
    /**
     * Set an object on the state.
     */
    [mutationTypes.UPDATE_SERVICES_LIFE_EXPECTANCY](state, {key, value}) {
        state.servicesLifeExpectancy[key] = value;
        if (key === 'dateOfDeath') {
            state.services.servicesEndDate = value;
        }
    },
    /**
     * Set an object on the state.
     */
    [mutationTypes.UPDATE_PURCHASES_LIFE_EXPECTANCY](state, {index, key, value}) {
        state.purchasesLifeExpectancy[key] = value;
        if (key === 'dateOfDeath') {
            state.purchases[index].purchaseEnd = value;
        }
    },
    /**
     * Add the purchase template to the array
     */
    [mutationTypes.ADD_PURCHASE](state) {
        state.purchases.push(purchaseTemplate());
    },
    /**
     * Add the care template to the array
     */
    [mutationTypes.ADD_CARE_PERIOD](state) {
        state.care.push(otherFutureLossesCareTemplate());
    },
    /**
     * Add the service template to the array
     */
    [mutationTypes.ADD_SERVICE](state) {
        state.services.push(otherFutureLossesServicesTemplate());
    },
    /**
     * Remove a purchase
     */
    [mutationTypes.REMOVE_PURCHASE](state, index) {
        state.purchases.splice(index, 1);
    },
    /**
     * Remove a care period
     */
    [mutationTypes.REMOVE_CARE_PERIOD](state, index) {
        state.care.splice(index, 1);
    },
    /**
     * Remove a service
     */
    [mutationTypes.REMOVE_SERVICE](state, index) {
        state.services.splice(index, 1);
    },
    /**
     * Clear the step from future losses
     */
    [mutationTypes.CLEAR_STEP](state) {
        state.futureLossOfCare = null;
        state.care = [];
        state.careLifeExpectancy = lifeExpectancyTemplate();
        state.futureLossOfServices = null;
        state.services = [];
        state.servicesLifeExpectancy = lifeExpectancyTemplate();
        state.futurePurchases = null;
        state.purchases = [];
        state.purchasesLifeExpectancy = lifeExpectancyTemplate();
        state.futureLossOfPension = null;
        state.pension.illHealthDisabilityPension = false;
        state.pension.pensionMethod = null;
        state.pension.dateLumpSumPaymentReceived = null;
        state.pension.butForRetirementDate = null;
        state.pension.retirementDate = null;
        state.pension.multiplierToRetirement = null;
        state.pension.lifeMultiplier = null;
        state.pension.discountFactor = null;
        state.pension.lumpSumLoss = null;
        state.pension.butForPensionLumpSum = 0;
        state.pension.pensionLumpSum = 0;
        state.pension.longdenFactor = null;
        state.pension.lumpSumLossValue = null;
        state.pension.lumpSumLossPresentValue = null;
        state.pension.butForPensionAnnual = 0;
        state.pension.pensionAnnual = 0;
        state.pension.pensionLossAnnual = null;
        state.pension.pensionLossMultiplier = null;
        state.pension.totalLossAnnual = null;
        state.pension.totalLoss = null;
        state.pension.acceleratedReceipt = null;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}


// WEBPACK FOOTER //
// ./src/store/modules/personal/otherFutureLosses.js